// A FITTING IN THE LENGTH OF A PIPE (3.19.0). Owner, 2026-10-21: "when I click on a pipe, I can right-click on any area in
// the length of that pipe and insert a fitting, such as a T-piece, or anything that fits in there. So it's got to be a
// pass-through, so mainly T-pieces. And can you please add in a set of butterfly valves that we can also insert".
//
// A pass-through fitting has two ends on one axis: a tee (its run), a tee with the branch up or down, a butterfly valve,
// a check valve. The right-click point is taken onto the centre line of the pipe; the fitting is centred there, its axis
// along the pipe. The pipe becomes TWO pipes:
//   - the pipe keeps end A and ends on the fitting end that faces end A;
//   - a new pipe goes from the other fitting end to where end B was, with end B as it was (attached or free, its look).
// A pipe that was joined to end B is joined to the new pipe. Both new joins are "joined" ends (no line across). The new
// pipe has the look of the pipe (its colour and lines), and the fitting takes the colour of a coloured pipe. ONE command.
// Pure: no store, no UI. tests/pipeInsert.test.ts covers it.
import type { Vec } from "../core/geometry";
import type { Doc, Entity, Style } from "../doc/entities";
import { applyCommand, type Command } from "../doc/commands";
import { makeFittingItem, PIPE_SIZES } from "../library/fittings";
import type { LibraryItem } from "../library/library";
import { checkValveItemFor, checkValveRowFor } from "../library/checkValves";
import { butterflyItemFor, butterflyRowFor, hasOperator, type ButterflyOperator } from "../library/butterflyValves";
import { pasteCommand } from "../tools/editOps";
import { rotatePatches } from "../tools/entityOps";
import { makePipeCommand, pipeEnds, pipeMetaOf, setPipePatches, type FittingPortData, type PipeAnchor } from "./api";
import { partsInColour, runColourOf } from "./runColour";

export type InsertGroup = "tee" | "tee-smaller" | "butterfly" | "checkvalve";

export interface InsertOption {
  /** What the menu shows, for example "Tee · branch to the top". */
  label: string;
  group: InsertGroup;
  kind: "tee" | "teeup" | "teedown" | "butterfly" | "checkvalve";
  /** The pipe size, mm. */
  size: number;
  /** The branch of a tee, mm. */
  size2?: number;
  operator?: ButterflyOperator;
  /** Butterfly valve: 100 = open, 0 = closed (3.22.0). */
  openPct?: number;
  /** The fitting end that faces end A of the pipe, and the end that faces end B. */
  through: [number, number];
  /** The branch end of a side tee (the Pipe tool can start there); null for the others. */
  branch: number | null;
}

/** The fitting when the right-click point is at least this far from each end of the pipe, mm. */
export const MIN_PIECE = 1;

const ang = (v: Vec): number => Math.atan2(v.y, v.x);
const rot = (v: Vec, a: number): Vec => ({ x: v.x * Math.cos(a) - v.y * Math.sin(a), y: v.x * Math.sin(a) + v.y * Math.cos(a) });

/** Where a direction points on the screen (Y down): "the right", "the top-left", ... */
export function screenWords(d: Vec): string {
  const names = ["the right", "the bottom-right", "the bottom", "the bottom-left", "the left", "the top-left", "the top", "the top-right"];
  const deg = ((ang(d) * 180) / Math.PI + 360) % 360;
  return names[Math.round(deg / 45) % 8]!;
}

function portsOfItem(item: LibraryItem): FittingPortData[] {
  const g = item.entities.find((e) => e.type === "group" && Array.isArray(e.meta?.ports)) as Entity | undefined;
  return (g?.meta?.ports as FittingPortData[] | undefined) ?? [];
}

/** The turn (radians) that makes fitting end `through[0]` face end A, for a pipe whose axis A -> B is `u`. */
function turnFor(item: LibraryItem, through: [number, number], u: Vec): number {
  return ang({ x: -u.x, y: -u.y }) - ang(portsOfItem(item)[through[0]]!.dir);
}

/** The library item of an option, or null when there is none of that size. */
export function insertItem(opt: Pick<InsertOption, "kind" | "size" | "size2" | "operator" | "openPct">): LibraryItem | null {
  switch (opt.kind) {
    case "tee":
    case "teeup":
    case "teedown":
      return makeFittingItem(opt.kind, opt.size, opt.size2);
    case "checkvalve":
      return checkValveItemFor(opt.size);
    case "butterfly":
      return butterflyItemFor(opt.size, { operator: opt.operator ?? "lever", openPct: opt.openPct ?? 100, view: "plan" });
  }
}

/**
 * What can go in a pipe of `size` whose axis from end A to end B is `u`: tees (the branch to each side, up and down;
 * smaller branches in their own group), ERA butterfly valves (lever DN50 to DN200, gearbox DN100 to DN400; open, closed) and check valves (each flow
 * direction), when there is one of this size. The words name the side on the screen.
 */
export function insertOptions(pipe: { size: number; u: Vec }): InsertOption[] {
  const s = pipe.size;
  const u = pipe.u;
  const out: InsertOption[] = [];
  const sideTees = (branch: number, group: InsertGroup): InsertOption[] => {
    const item = makeFittingItem("tee", s, branch);
    return ([[0, 1], [1, 0]] as const).map((through) => {
      const d = rot(portsOfItem(item)[2]!.dir, turnFor(item, [through[0], through[1]], u));
      const where = screenWords(d);
      return {
        label: branch === s ? `Tee · branch to ${where}` : `Tee · branch ${branch} mm to ${where}`,
        group,
        kind: "tee" as const,
        size: s,
        size2: branch,
        through: [through[0], through[1]] as [number, number],
        branch: 2
      };
    });
  };
  const upDown = (branch: number, group: InsertGroup): InsertOption[] =>
    (["teeup", "teedown"] as const).map((kind) => ({
      label: branch === s ? `Tee · branch ${kind === "teeup" ? "up (riser)" : "down (drop)"}` : `Tee · branch ${branch} mm ${kind === "teeup" ? "up" : "down"}`,
      group,
      kind,
      size: s,
      size2: branch,
      through: [0, 1] as [number, number],
      branch: null
    }));
  out.push(...sideTees(s, "tee"), ...upDown(s, "tee"));
  for (const b of [...PIPE_SIZES].filter((x) => x < s).sort((a, c) => c - a)) out.push(...sideTees(b, "tee-smaller"), ...upDown(b, "tee-smaller"));

  const bf = butterflyRowFor(s);
  if (bf) {
    for (const operator of ["lever", "gearbox"] as const) {
      if (!hasOperator(bf, operator)) continue;
      for (const openPct of [100, 0]) {
        out.push({ label: `Butterfly valve · DN${s} · ${operator} · ${openPct ? "open" : "closed"}`, group: "butterfly", kind: "butterfly", size: s, operator, openPct, through: [0, 1], branch: null });
      }
    }
  }
  if (checkValveRowFor(s)) {
    out.push(
      { label: `Check valve · DN${s} · flow to ${screenWords(u)}`, group: "checkvalve", kind: "checkvalve", size: s, through: [0, 1], branch: null },
      { label: `Check valve · DN${s} · flow to ${screenWords({ x: -u.x, y: -u.y })}`, group: "checkvalve", kind: "checkvalve", size: s, through: [1, 0], branch: null }
    );
  }
  return out;
}

/** The axis of pipe `pipeId` from end A to end B, its ends, its length and its size; null for a pipe with no length. */
export function pipeAxis(doc: Doc, pipeId: string): { a: Vec; b: Vec; u: Vec; length: number; size: number } | null {
  const pm = pipeMetaOf(doc.entities[pipeId]);
  const ends = pipeEnds(doc, pipeId);
  if (!pm || !ends) return null;
  const length = Math.hypot(ends.b.x - ends.a.x, ends.b.y - ends.a.y);
  if (length < 1e-6) return null;
  return { a: ends.a, b: ends.b, u: { x: (ends.b.x - ends.a.x) / length, y: (ends.b.y - ends.a.y) / length }, length, size: pm.size };
}

/**
 * ONE command that puts the fitting of `opt` in pipe `pipeId` at the point of its centre line nearest `at`, and splits the
 * pipe on it. A sentence instead when it cannot (the pipe is too short for the fitting, or there is no such fitting).
 * `newPipeId` is the pipe from the fitting to the old end B; `branch` is the free branch end of a side tee.
 */
export function insertFittingCommand(
  doc: Doc,
  pipeId: string,
  at: Vec,
  opt: InsertOption
): { cmd: Command; fittingId: string; newPipeId: string; branch: number | null; centre: Vec } | string {
  const g = doc.entities[pipeId];
  const pm = pipeMetaOf(g);
  const ax = pipeAxis(doc, pipeId);
  if (!g || g.type !== "group" || !pm || !ax) return "Select a pipe that has a length.";
  if (opt.size !== pm.size) return `This fitting is for a ${opt.size} mm pipe. The pipe is ${pm.size} mm.`;
  const item = insertItem(opt);
  if (!item) return `TraceCAD has no such fitting of ${pm.size} mm.`;
  const ports = portsOfItem(item);
  const q0 = ports[opt.through[0]];
  const q1 = ports[opt.through[1]];
  if (!q0 || !q1) return "This fitting has no ends that go in a pipe.";
  const run = Math.hypot(q1.p.x - q0.p.x, q1.p.y - q0.p.y);
  const need = run + 2 * MIN_PIECE;
  if (ax.length < need) return `The pipe is ${Math.round(ax.length)} mm long. This fitting needs ${Math.ceil(need)} mm. Make the pipe longer first.`;

  // the point on the centre line, kept far enough from each end that the fitting fits
  const t0 = (at.x - ax.a.x) * ax.u.x + (at.y - ax.a.y) * ax.u.y;
  const t = Math.min(ax.length - run / 2 - MIN_PIECE, Math.max(run / 2 + MIN_PIECE, t0));
  const centre = { x: ax.a.x + ax.u.x * t, y: ax.a.y + ax.u.y * t };
  const mid = { x: (q0.p.x + q1.p.x) / 2, y: (q0.p.y + q1.p.y) / 2 };

  const layerId = g.layerId;
  const commands: Command[] = [];
  const draft = structuredClone(doc);
  const step = (cmd: Command | null) => {
    if (!cmd) return;
    applyCommand(draft, cmd);
    commands.push(cmd);
  };
  // 1. the fitting, its run centre on the point, in the colour of a coloured pipe
  const paste = pasteCommand({ entities: partsInColour(item.entities, runColourOf(doc, pipeId)) }, layerId, { x: centre.x - mid.x, y: centre.y - mid.y });
  if (!paste) return "The fitting could not be placed.";
  step(paste.cmd);
  const fittingId = paste.newIds.find((id) => draft.entities[id]?.type === "group" && Array.isArray(draft.entities[id]?.meta?.ports));
  if (!fittingId) return "The fitting could not be placed.";
  // 2. turned so its run lies along the pipe, end through[0] facing end A
  const deg = (turnFor(item, opt.through, ax.u) * 180) / Math.PI;
  const turned = Math.abs(((deg % 360) + 360) % 360) < 1e-9 ? [] : rotatePatches(draft, [fittingId], centre, deg);
  if (turned.length) step({ kind: "updateEntities", patches: turned });
  // 3. the new pipe: from the other end of the fitting to where end B is, end B as it is
  const made = makePipeCommand(draft, layerId, {
    size: pm.size,
    a: { kind: "port", ownerId: fittingId, port: opt.through[1] },
    b: structuredClone(pm.b),
    endA: "none",
    endB: pm.endB
  });
  keepLook(made.cmd, doc, pipeId);
  step(made.cmd);
  // 4. the pipes joined to end B of the pipe are joined to the new pipe
  for (const e of Object.values(draft.entities)) {
    const om = pipeMetaOf(e);
    if (!om || e.id === pipeId || e.id === made.groupId) continue;
    const change: { a?: PipeAnchor; b?: PipeAnchor } = {};
    if (om.a.kind === "port" && om.a.ownerId === pipeId && om.a.port === 1) change.a = { kind: "port", ownerId: made.groupId, port: 1 };
    if (om.b.kind === "port" && om.b.ownerId === pipeId && om.b.port === 1) change.b = { kind: "port", ownerId: made.groupId, port: 1 };
    if (change.a || change.b) {
      const ps = setPipePatches(draft, e.id, change);
      if (ps.length) step({ kind: "updateEntities", patches: ps });
    }
  }
  // 5. the pipe ends on the fitting end that faces end A
  const cut = setPipePatches(draft, pipeId, { b: { kind: "port", ownerId: fittingId, port: opt.through[0] }, endB: "none" });
  if (cut.length) step({ kind: "updateEntities", patches: cut });
  return { cmd: { kind: "batch", label: "insert fitting", commands }, fittingId, newPipeId: made.groupId, branch: opt.branch, centre };
}

/** The new pipe takes the look of the pipe it is cut from: each line the style of the same line of the old pipe. */
function keepLook(cmd: Command, doc: Doc, pipeId: string): void {
  const g = doc.entities[pipeId];
  if (!g || g.type !== "group" || cmd.kind !== "batch") return;
  const add = cmd.commands.find((c) => c.kind === "addEntities");
  if (!add || add.kind !== "addEntities") return;
  add.entities.forEach((e, i) => {
    const old = doc.entities[g.children[i] ?? ""];
    if (old && "style" in old && "style" in e) (e as Entity & { style: Style }).style = structuredClone((old as Entity & { style: Style }).style);
  });
}
